import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Check, Ship, Users, Wallet, FileText, CalendarClock, Flag, Plus, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp, dEN } from "@/lib/format";
import { Card, PageHeader } from "@/components/admin/common";
import AvailabilityCalendar from "@/components/admin/AvailabilityCalendar";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Checkbox } from "@/components/ui/checkbox";

const RANGES = [["month", "This month"], ["year", "This year"], ["all", "All time"]];
const STATS = [
  ["trips_completed", "Trips completed"], ["upcoming_trips", "Upcoming trips"], ["total_bookings", "Total bookings"],
  ["total_guests", "Total guests"], ["income", "Income received", true], ["expenses", "Expenses", true],
  ["net_profit", "Net profit", true], ["owed_by_guests", "Owed by guests", true], ["owed_to_vendors", "Owed to boat vendors", true],
];
const KIND = {
  trip: [CalendarClock, "bg-sky-100 text-sky-700"], complete: [Flag, "bg-violet-100 text-violet-700"],
  balance: [Wallet, "bg-amber-100 text-amber-700"], vendor: [Ship, "bg-orange-100 text-orange-700"],
  manifest: [Users, "bg-rose-100 text-rose-700"], invoice: [FileText, "bg-slate-200 text-slate-700"],
};

const Stats = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
    {STATS.map(([k, label, money]) => (
      <Card key={k} className="p-4" data-testid={`stat-${k}`}>
        <p className="text-xs font-semibold text-slate-500">{label}</p>
        <p className={`mt-1 font-extrabold ${money ? "text-lg sm:text-xl" : "text-2xl"} ${k === "net_profit" && stats[k] < 0 ? "text-rose-600" : ""}`}>
          {money ? rp(stats[k]) : stats[k]}
        </p>
      </Card>
    ))}
  </div>
);

const Reminders = ({ items, onDismiss }) => {
  const navigate = useNavigate();
  if (!items.length) return <Card className="p-6 text-center text-slate-500" data-testid="reminders-empty">All clear for this week.</Card>;
  return (
    <div className="space-y-2" data-testid="reminders-list">
      {items.map((r) => {
        const [Icon, cls] = KIND[r.kind] || KIND.trip;
        return (
          <Card key={r.key} className="flex items-center gap-3 p-3 pr-2" data-testid={`reminder-${r.key}`}>
            <button className="flex items-center gap-3 flex-1 min-w-0 text-left" onClick={() => navigate(r.link)} data-testid={`reminder-open-${r.key}`}>
              <span className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${cls}`}><Icon className="w-5 h-5" /></span>
              <span className="min-w-0">
                <span className="block font-bold text-[15px]">{r.title}</span>
                <span className="block text-sm text-slate-500 truncate">{r.detail} · {dEN(r.date)}</span>
              </span>
              <ChevronRight className="w-4 h-4 text-slate-400 ml-auto shrink-0" />
            </button>
            <button onClick={() => onDismiss(r.key)} className="w-11 h-11 rounded-xl border border-border grid place-items-center hover:bg-soft shrink-0" aria-label="Done" data-testid={`reminder-done-${r.key}`}>
              <Check className="w-5 h-5" />
            </button>
          </Card>
        );
      })}
    </div>
  );
};

const Todos = () => {
  const [todos, setTodos] = useState([]);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const load = () => api.get("/admin/todos").then((r) => setTodos(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await api.post("/admin/todos", { title, due_date: due });
    setTitle(""); setDue(""); load();
  };
  const toggle = async (t) => { await api.put(`/admin/todos/${t.id}`, { ...t, done: !t.done }); load(); };
  const remove = async (t) => { await api.delete(`/admin/todos/${t.id}`); load(); };
  return (
    <Card className="p-4 space-y-3" data-testid="todo-card">
      <form onSubmit={add} className="flex flex-wrap gap-2">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task, e.g. reply to John" className="h-12 flex-1 min-w-[180px]" data-testid="todo-input" />
        <DateInput value={due} onChange={setDue} className="w-44" testid="todo-due-input" />
        <button className="h-12 w-12 rounded-xl bg-ocean text-white grid place-items-center" data-testid="todo-add-btn" aria-label="Add task"><Plus /></button>
      </form>
      {todos.map((t) => (
        <div key={t.id} className="flex items-center gap-3 py-2 border-t border-border first:border-0" data-testid={`todo-${t.id}`}>
          <Checkbox checked={t.done} onCheckedChange={() => toggle(t)} className="w-6 h-6" data-testid={`todo-check-${t.id}`} />
          <span className={`flex-1 ${t.done ? "line-through text-slate-400" : ""}`}>{t.title}</span>
          {t.due_date && <span className="text-xs text-slate-500">{dEN(t.due_date)}</span>}
          <button onClick={() => remove(t)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`todo-delete-${t.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </Card>
  );
};

export default function Dashboard() {
  const { user } = useOutletContext();
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/dashboard", { params: { range } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [range]);
  useEffect(() => { load(); }, [load]);
  const dismiss = async (key) => { await api.post(`/admin/reminders/${key}/dismiss`); toast.success("Marked as done"); load(); };

  return (
    <div className="space-y-8 max-w-6xl" data-testid="dashboard-page">
      <PageHeader title={`Hi, ${(user.name || "").split(" ")[0] || "there"}`} subtitle="Here's what needs you this week." />
      <section className="space-y-3">
        <h2 className="text-lg font-extrabold">This week's reminders</h2>
        {data ? <Reminders items={data.reminders} onDismiss={dismiss} /> : <Card className="h-24 animate-pulse" />}
      </section>
      <section className="space-y-3">
        <h2 className="text-lg font-extrabold">My to-do list</h2>
        <Todos />
      </section>
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-extrabold">Numbers at a glance</h2>
          <div className="flex bg-white border border-border rounded-xl p-1" data-testid="range-switch">
            {RANGES.map(([k, l]) => (
              <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
                className={`px-3 h-9 rounded-lg text-sm font-semibold ${range === k ? "bg-ocean text-white" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
        </div>
        {data && <Stats stats={data.stats} />}
      </section>
      <section className="space-y-3">
        <h2 className="text-lg font-extrabold">Availability</h2>
        <AvailabilityCalendar />
      </section>
    </div>
  );
}
